'use client';

import { c as _c } from "react/compiler-runtime";
import type { ListPopularPolicyDto } from '__generated__/api/auth/types.gen';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AuthQueryOptions, UnauthQueryOptions } from '@/api';
import { DesktopPageShell } from '@/components/template/DesktopPageShell';
import { useAuthStore } from '@/stores/AuthStore';
import type { PolicyFilterState, PolicySortOption } from '../../_hooks/usePolicyFilter';
import { EMPTY_AI_SUMMARY_MESSAGE, EMPTY_SUPPORT_BENEFIT_MESSAGE, PAGE_SIZE, PREVIEW_SIZE } from '../../constants';
import { buildPaginationMeta, formatApplicationPeriod } from '../../utils';
import { PolicyFilter } from './PolicyFilter';
import { PolicyResultList } from './PolicyResultList';
import type { PopularPolicyData, PopularPolicySource } from './PopularPolicies';
import { PopularPolicies } from './PopularPolicies';
const getDdayDisplay = (dday?: string) => {
  const ddayText = dday?.replace('마감 ', '') ?? '상시';
  const ddayMatch = ddayText.match(/D-(\d+)/);
  const isUrgent = Boolean(ddayMatch && Number(ddayMatch[1]) <= 7);
  return {
    ddayText,
    isUrgent
  };
};
const mapPopularPolicy = (policy: ListPopularPolicyDto, source?: PopularPolicySource): PopularPolicyData => ({
  id: policy.metaPolicyIdIdx,
  title: policy.policyName,
  benefit: policy.supportBenefit ?? EMPTY_SUPPORT_BENEFIT_MESSAGE,
  source,
  ...getDdayDisplay(policy.dday)
});
const interleave = <T,>(a: T[], b: T[]): T[] => {
  const merged: T[] = [];
  const max = Math.max(a.length, b.length);
  for (let index = 0; index < max; index++) {
    if (index < a.length) merged.push(a[index]);
    if (index < b.length) merged.push(b[index]);
  }
  return merged;
};
const dedupePopularPolicies = (policies: PopularPolicyData[]) => {
  const seen = new Set<string>();
  return policies.filter(policy => {
    if (seen.has(policy.id)) return false;
    seen.add(policy.id);
    return true;
  });
};
interface PolicySearchProps {
  isFilterOpen: boolean;
  onCloseFilter: () => void;
  state: PolicyFilterState;
  onToggleTargetCode: (code: string) => void;
  onToggleProvisionCode: (code: string) => void;
  onToggleJobCode: (code: string) => void;
  onToggleAgeCode: (code: string) => void;
  onResetFilters: () => void;
  onSortChange: (sort: PolicySortOption) => void;
  onPageChange: (page: number) => void;
}
export function PolicySearch(t0) {
  const $ = _c(48);
  const {
    isFilterOpen,
    onCloseFilter,
    state,
    onToggleTargetCode,
    onToggleProvisionCode,
    onToggleJobCode,
    onToggleAgeCode,
    onResetFilters,
    onSortChange,
    onPageChange
  } = t0;
  const isLogin = useAuthStore(_temp);
  let t1;
  if ($[0] !== state.ageCodes || $[1] !== state.jobCodes || $[2] !== state.keyword || $[3] !== state.page || $[4] !== state.provisionCodes || $[5] !== state.sort || $[6] !== state.targetCodes) {
    t1 = UnauthQueryOptions["get:/wello/v2/allows/query/policies"]({
      query: {
        searchKeyword: state.keyword || undefined,
        targetCodeList: state.targetCodes.length > 0 ? state.targetCodes : undefined,
        provisionCodeList: state.provisionCodes.length > 0 ? state.provisionCodes : undefined,
        jobCodeList: state.jobCodes.length > 0 ? state.jobCodes : undefined,
        descAgeList: state.ageCodes.length > 0 ? state.ageCodes : undefined,
        policySort: state.sort,
        page: state.page,
        size: PAGE_SIZE
      }
    });
    $[0] = state.ageCodes;
    $[1] = state.jobCodes;
    $[2] = state.keyword;
    $[3] = state.page;
    $[4] = state.provisionCodes;
    $[5] = state.sort;
    $[6] = state.targetCodes;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== t1) {
    t2 = {
      ...t1,
      placeholderData: keepPreviousData,
      select: _temp3
    };
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const {
    data: policyData,
    isLoading: isPolicyLoading
  } = useQuery(t2);
  let t3;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = AuthQueryOptions["get:/wello/v2/auth/member/peer-group/policy"]();
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const t4 = Boolean(isLogin);
  let t5;
  if ($[11] !== t4) {
    t5 = {
      ...t3,
      enabled: t4,
      select: _temp5
    };
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const {
    data: t6
  } = useQuery(t5);
  const peerPolicies = t6 === undefined ? [] : t6;
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = AuthQueryOptions["get:/wello/v2/auth/member/neighbor/policy"]();
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = Boolean(isLogin);
  let t9;
  if ($[14] !== t8) {
    t9 = {
      ...t7,
      enabled: t8,
      select: _temp7
    };
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const {
    data: t10
  } = useQuery(t9);
  const neighborPolicies = t10 === undefined ? [] : t10;
  let t11;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = UnauthQueryOptions["get:/wello/v2/allows/policy/popular"]();
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const t12 = isLogin === false;
  let t13;
  if ($[17] !== t12) {
    t13 = {
      ...t11,
      enabled: t12,
      select: _temp9
    };
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const {
    data: t14
  } = useQuery(t13);
  const anonymousPopularPolicies = t14 === undefined ? [] : t14;
  const popularPolicies = dedupePopularPolicies(isLogin ? interleave(peerPolicies, neighborPolicies) : anonymousPopularPolicies).slice(0, PREVIEW_SIZE);
  let t15;
  if ($[19] !== isFilterOpen || $[20] !== onCloseFilter || $[21] !== onResetFilters || $[22] !== onToggleAgeCode || $[23] !== onToggleJobCode || $[24] !== onToggleProvisionCode || $[25] !== onToggleTargetCode || $[26] !== state.ageCodes || $[27] !== state.jobCodes || $[28] !== state.provisionCodes || $[29] !== state.targetCodes) {
    t15 = <PolicyFilter isDrawerOpen={isFilterOpen} onCloseDrawer={onCloseFilter} onApplyDrawer={onCloseFilter} targetCodes={state.targetCodes} provisionCodes={state.provisionCodes} jobCodes={state.jobCodes} ageCodes={state.ageCodes} onToggleTargetCode={onToggleTargetCode} onToggleProvisionCode={onToggleProvisionCode} onToggleJobCode={onToggleJobCode} onToggleAgeCode={onToggleAgeCode} onReset={onResetFilters} />;
    $[19] = isFilterOpen;
    $[20] = onCloseFilter;
    $[21] = onResetFilters;
    $[22] = onToggleAgeCode;
    $[23] = onToggleJobCode;
    $[24] = onToggleProvisionCode;
    $[25] = onToggleTargetCode;
    $[26] = state.ageCodes;
    $[27] = state.jobCodes;
    $[28] = state.provisionCodes;
    $[29] = state.targetCodes;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== popularPolicies) {
    t16 = <PopularPolicies policies={popularPolicies} />;
    $[31] = popularPolicies;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== policyData?.policies) {
    t17 = policyData?.policies ?? [];
    $[33] = policyData?.policies;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  const t18 = policyData?.totalCount ?? 0;
  const t19 = policyData?.totalPages ?? 0;
  let t20;
  if ($[35] !== isPolicyLoading || $[36] !== onPageChange || $[37] !== onSortChange || $[38] !== state.page || $[39] !== state.sort || $[40] !== t17 || $[41] !== t18 || $[42] !== t19) {
    t20 = <PolicyResultList policies={t17} totalCount={t18} sort={state.sort} onSortChange={onSortChange} page={state.page} totalPages={t19} onPageChange={onPageChange} isLoading={isPolicyLoading} emptyMessage={"\uC870\uAC74\uC5D0 \uB9DE\uB294 \uC815\uCC45\uC774 \uC5C6\uC2B5\uB2C8\uB2E4"} />;
    $[35] = isPolicyLoading;
    $[36] = onPageChange;
    $[37] = onSortChange;
    $[38] = state.page;
    $[39] = state.sort;
    $[40] = t17;
    $[41] = t18;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t15 || $[45] !== t16 || $[46] !== t20) {
    t21 = <DesktopPageShell className="mt-20 w-full mobile-tablet:mt-0" leftAside={t15} rightAside={t16}>{t20}</DesktopPageShell>;
    $[44] = t15;
    $[45] = t16;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  return t21;
}
function _temp9(data_2) {
  return (data_2.context?.policyList ?? []).map(_temp8);
}
function _temp8(policy_2) {
  return mapPopularPolicy(policy_2);
}
function _temp7(data_1) {
  return (data_1.context?.policyList ?? []).map(_temp6);
}
function _temp6(policy_1) {
  return mapPopularPolicy(policy_1, "neighbor");
}
function _temp5(data_0) {
  return (data_0.context?.policyList ?? []).map(_temp4);
}
function _temp4(policy_0) {
  return mapPopularPolicy(policy_0, "peer");
}
function _temp3(data) {
  const context = data.context;
  return {
    policies: context?.contents?.map(_temp2) ?? [],
    ...buildPaginationMeta(context?.totalCount ?? 0)
  };
}
function _temp2(policy) {
  return {
    id: policy.metaPolicyIdIdx ?? String(policy.id ?? ""),
    slugId: policy.slugId,
    contentId: policy.id,
    title: policy.policyName ?? "",
    aiSummary: policy.aiSummary ?? EMPTY_AI_SUMMARY_MESSAGE,
    applicationPeriod: formatApplicationPeriod(policy.dueDateStart, policy.dueDateEnd),
    agencyName: policy.agency ?? "",
    agencyLogo: policy.agencyLogo ?? "",
    isBookmarked: Boolean(policy.wishlistYN)
  };
}
function _temp(state_0) {
  return state_0.isLogin;
}